import {Box, BoxProps, Typography} from '@mui/material';
import {AppButton, AppIconButton} from 'components/buttons';
import {useSessionRedux} from 'hooks';
import {useCreateSetting, useUpdateSetting} from 'hooks/settings';
import {Add} from 'iconsax-react';
import {FieldValues, ItemDataFieldModel} from 'models/board';
import {SettingCreateRequest, SettingModel, SettingsUpdateRequest, SettingsUpdateRequestModel} from 'models/settings';
import React, {useEffect} from 'react';
import {useForm} from 'react-hook-form';
import {FieldsRenderer} from 'views/board/drawers/BoardItemDrawer/FieldsRenderer';

interface DynamiclyFeaturesRenderingProps {
    settings: SettingModel[];
    onCloseHanlder: () => void;
    containerProps?: BoxProps;
    shouldUpdateTenantSettings?: boolean;
    getAllSettingsRequest: () => void;
    loading?: boolean;
}
export const DynamiclySettingsRendering: React.FC<DynamiclyFeaturesRenderingProps> = ({
    settings,
    onCloseHanlder,
    containerProps,
    shouldUpdateTenantSettings = true,
    getAllSettingsRequest,
    loading,
}) => {
    console.log('settings', settings);

    const {updateTenantSettingValue} = useSessionRedux();
    const {setValue, control, watch, handleSubmit} = useForm<SettingsUpdateRequestModel>({
        defaultValues: {tenantSlug: '', settings: []},
    });
    const {createSetting, createSettingLoading, createSettingResponse} = useCreateSetting();
    const {updateSetting, updateSettingLoading, updateSettingResponse} = useUpdateSetting();
    useSessionRedux();
    const handleSubmitForm = (data: SettingsUpdateRequestModel) => {
        updateSetting({
            variables: {
                input: data,
            },
        });
    };

    useEffect(() => {
        if (settings) {
            setValue('tenantSlug', '');
            setValue(
                'settings',
                settings.map(x => {
                    return {id: x.id, data: x.data} as SettingsUpdateRequest;
                }),
            );
        }
    }, [settings]);

    useEffect(() => {
        if (updateSettingResponse && !updateSettingLoading && shouldUpdateTenantSettings) {
            const allValues: ItemDataFieldModel[] = updateSettingResponse.reduce(
                (acc: ItemDataFieldModel[], curr: any) => {
                    if (curr.data) {
                        acc.push(...curr.data.filter((item: any): item is ItemDataFieldModel => item !== undefined));
                    }
                    return acc;
                },
                [],
            );

            updateTenantSettingValue(allValues);
        }
        onCloseHanlder();
    }, [updateSettingResponse, updateSettingLoading]);

    const onAddClickHandler = (setting: SettingModel) => {
        const settingsNeededToAdd: SettingCreateRequest = {
            dataType: setting.dataType,
            title: setting.title,
            uniqueId: setting.uniqueId,
            objectId: setting.objectId,
            objectType: setting.objectType,
            value: setting.value,
            groupName: setting.groupName,
            controlType: setting.controlType,
            order: setting.order,
            tenantId: setting.tenantId,
            isMulti: setting.isMulti,
            data:
                setting.data?.map(field => ({
                    ...field,
                    value: {
                        value: '',
                        booleanValue: null,
                        selectedOption: undefined,
                        multiSelectedOptions: [],
                    } as FieldValues,
                })) || [],
        };
        createSetting({
            variables: {
                input: settingsNeededToAdd,
            },
        });
    };
    useEffect(() => {
        if (createSettingResponse && !createSettingLoading) {
            getAllSettingsRequest();
        }
    }, [createSettingLoading, createSettingResponse]);

    return (
        <Box
            display={'flex'}
            flexDirection={'column'}
            component={'form'}
            noValidate
            autoComplete="off"
            justifyContent={'space-between'}
            height={'calc(100vh - 180px)'}
            onSubmit={handleSubmit(handleSubmitForm)}
            {...containerProps}
        >
            <Box height={'calc(100vh - 230px)'} overflow={'auto'}>
                {settings.map((setting, index) => {
                    return (
                        <Box key={setting.uniqueId} mb={1} mt={1}>
                            <Box display={'flex'} justifyContent={'space-between'} alignItems={'center'}>
                                <Typography variant="subtitle1">{setting.title}</Typography>
                                {setting.isMulti && (
                                    <AppIconButton
                                        isLoading={createSettingLoading || loading}
                                        tooltip="Add more settings"
                                        variant="outlined"
                                        onClick={() => {
                                            onAddClickHandler(setting);
                                        }}
                                    >
                                        <Add />
                                    </AppIconButton>
                                )}
                            </Box>

                            {setting.data && setting.data.length > 0 ? (
                                <Box gap={1}>
                                    {setting.data.map((field, fieldIndex) => (
                                        <Box key={`${field.uniqueId}`} mt={1}>
                                            <FieldsRenderer
                                                field={field}
                                                control={control}
                                                setValue={setValue}
                                                indexKey={fieldIndex}
                                                watch={watch}
                                                gridSize={12}
                                                baseFieldName={`settings.${index}.data.${fieldIndex}.value`}
                                            />
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography color="text.secondary" mt={1}>
                                    No fields for this setting.
                                </Typography>
                            )}
                            {index < settings.length - 1 && (
                                <Box>
                                    <hr style={{border: 'none', borderTop: '1px solid #e0e0e0'}} />
                                </Box>
                            )}
                        </Box>
                    );
                })}
            </Box>
            <Box display={'flex'} justifyContent={'flex-end'}>
                <AppButton isLoading={updateSettingLoading} type="submit">
                    Save
                </AppButton>
            </Box>
        </Box>
    );
};

export default DynamiclySettingsRendering;
